import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  LifeBuoy, Plus, MessageSquare, Send, X, Inbox, Loader2, AlertCircle,
  HelpCircle, MessageCircleQuestion, Bug, Building2, CheckCircle2, Clock3,
  Paperclip, Download, FileText, FileSpreadsheet, FileImage, File as FileIcon,
} from "lucide-react";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import ConfirmModal from "../components/ConfirmModal";
import { FilePreviewModal } from "../components/common/FilePreviewModal";
import { UserAvatar } from "../components/UserAvatar";

// ───────────────────────────────────────────────────────────────────
// Costanti & helpers
// ───────────────────────────────────────────────────────────────────

const CATEGORY = {
  problema:  { label: "Problema",  icon: Bug,                  color: "text-red-600 bg-red-50 border-red-200" },
  richiesta: { label: "Richiesta", icon: MessageCircleQuestion, color: "text-blue-600 bg-blue-50 border-blue-200" },
  domanda:   { label: "Domanda",   icon: HelpCircle,           color: "text-amber-600 bg-amber-50 border-amber-200" },
};

const STATUS = {
  aperto:         { label: "Aperto",         className: "wl-badge wl-badge--warning" },
  in_lavorazione: { label: "In lavorazione", className: "wl-badge wl-badge--info" },
  risolto:        { label: "Risolto",        className: "wl-badge wl-badge--success" },
};

// ─── Attachments ────────────────────────────────────────────────────
const MAX_ATTACHMENT_SIZE = 15 * 1024 * 1024; // 15 MB (limite fine per tipo lato server)
const MAX_ATTACHMENTS_PER_MESSAGE = 5;
const ALLOWED_ATTACHMENT_EXTS = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".csv", ".png", ".jpg", ".jpeg"];
const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTS.join(",");
const ALLOWED_ATTACHMENT_MIMES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/jpg",
]);

const fmtFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const pickAttachmentIcon = (mimeOrName = "") => {
  const s = String(mimeOrName).toLowerCase();
  if (s.includes("image") || s.endsWith(".png") || s.endsWith(".jpg") || s.endsWith(".jpeg")) return FileImage;
  if (s.includes("pdf") || s.endsWith(".pdf")) return FileText;
  if (s.includes("word") || s.endsWith(".doc") || s.endsWith(".docx")) return FileText;
  if (s.includes("excel") || s.includes("sheet") || s.endsWith(".xls") || s.endsWith(".xlsx")) return FileSpreadsheet;
  return FileIcon;
};

const validateAttachmentFile = (file) => {
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? "." + name.split(".").pop() : "";
  const mime = (file.type || "").toLowerCase();
  const extOk = ALLOWED_ATTACHMENT_EXTS.includes(ext);
  const mimeOk = ALLOWED_ATTACHMENT_MIMES.has(mime);
  if (!extOk && !mimeOk) {
    return "Formato non supportato. Puoi caricare PDF, Word, Excel, CSV e immagini.";
  }
  if (file.size === 0) return "File vuoto.";
  if (file.size > MAX_ATTACHMENT_SIZE) {
    return `File troppo grande (max ${MAX_ATTACHMENT_SIZE / (1024 * 1024)} MB).`;
  }
  return null;
};

/**
 * Upload di ogni file allegato al messaggio dato. Esegue in serie per
 * segnalare toast progressivi e non intasare il backend. Non blocca l'esito
 * complessivo dell'invio del messaggio: gli errori vengono riportati come
 * toast e la funzione ritorna il numero di file caricati con successo.
 */
const uploadTicketAttachments = async (ticketId, messageId, files) => {
  let ok = 0;
  for (const f of files) {
    const fd = new FormData();
    fd.append("ticket_id", ticketId);
    fd.append("message_id", messageId);
    fd.append("file", f);
    try {
      await api.post("/support/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      ok += 1;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Errore caricamento "${f.name}".`);
    }
  }
  return ok;
};

const downloadAttachment = async (fileId, filename) => {
  try {
    const res = await api.get(`/support/attachments/${fileId}`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `allegato_${fileId.slice(0, 8)}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Errore download allegato.");
  }
};

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const fmtRel = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} g fa`;
  return fmtDateTime(iso);
};


// ───────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, className: "wl-badge wl-badge--neutral" };
  return (
    <span
      data-testid={`status-${status}`}
      className={s.className}
    >
      {s.label}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const c = CATEGORY[category];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${c.color}`}>
      <Icon size={11} strokeWidth={2} />
      {c.label}
    </span>
  );
};

const Section = ({ title, icon: Icon, children, action }) => (
  <div className="bg-surface border border-border rounded-lg p-5">
    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} strokeWidth={2} className="text-accent" />}
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);


// ───────────────────────────────────────────────────────────────────
// Attachments UI
// ───────────────────────────────────────────────────────────────────

/**
 * Picker riutilizzabile: aggiungi/rimuovi file lato client, con validazione
 * di formato e dimensione. Il caller riceve la lista aggiornata via setFiles.
 */
const AttachmentPicker = ({ files, setFiles, disabled = false, testIdPrefix = "att" }) => {
  const inputRef = useRef(null);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    // Reset dell'input così l'utente può ri-selezionare lo stesso file dopo remove
    if (inputRef.current) inputRef.current.value = "";
    if (!picked.length) return;

    const nextList = [...files];
    for (const f of picked) {
      if (nextList.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
        toast.error(`Massimo ${MAX_ATTACHMENTS_PER_MESSAGE} allegati per messaggio.`);
        break;
      }
      const err = validateAttachmentFile(f);
      if (err) { toast.error(`"${f.name}": ${err}`); continue; }
      // De-dup per nome+size (stessa selezione ripetuta)
      if (nextList.some((x) => x.name === f.name && x.size === f.size)) continue;
      nextList.push(f);
    }
    setFiles(nextList);
  };

  const removeAt = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const remaining = MAX_ATTACHMENTS_PER_MESSAGE - files.length;

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || remaining <= 0}
          data-testid={`${testIdPrefix}-pick-btn`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-subtle hover:border-accent text-xs font-medium text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Paperclip size={13} strokeWidth={2} />
          Allega file
        </button>
        <span className="text-[11px] text-text-muted">
          {files.length}/{MAX_ATTACHMENTS_PER_MESSAGE} · PDF, Word, Excel, CSV, immagini · max 25 MB (immagini 10 MB)
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={onPick}
          className="hidden"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-1" data-testid={`${testIdPrefix}-list`}>
          {files.map((f, idx) => {
            const Icon = pickAttachmentIcon(f.type || f.name);
            return (
              <li
                key={`${f.name}-${f.size}-${idx}`}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-subtle text-xs"
                data-testid={`${testIdPrefix}-item-${idx}`}
              >
                <Icon size={13} className="text-accent flex-shrink-0" />
                <span className="flex-1 truncate text-text-primary">{f.name}</span>
                <span className="text-text-muted whitespace-nowrap">{fmtFileSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  disabled={disabled}
                  className="text-text-muted hover:text-red-500 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                  title="Rimuovi"
                >
                  <X size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/**
 * Renderizza gli allegati di un messaggio: click sul nome = anteprima inline,
 * icona download = scarica (stessa route, stessi permessi).
 */
const AttachmentList = ({ attachments = [] }) => {
  const [preview, setPreview] = useState(null);
  if (!attachments.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="msg-attachments">
      {attachments.map((a) => {
        const Icon = pickAttachmentIcon(a.mime || a.filename);
        return (
          <button
            key={a.file_id}
            type="button"
            onClick={() => setPreview(a)}
            data-testid={`attachment-${a.file_id}`}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-surface hover:border-accent hover:bg-accent/5 text-[11px] text-text-primary transition-colors max-w-full"
            title={`Visualizza ${a.filename}`}
          >
            <Icon size={12} className="text-accent flex-shrink-0" />
            <span className="truncate max-w-[180px]">{a.filename}</span>
            <span className="text-text-muted whitespace-nowrap">{fmtFileSize(a.size)}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); downloadAttachment(a.file_id, a.filename); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); downloadAttachment(a.file_id, a.filename); } }}
              data-testid={`attachment-dl-${a.file_id}`}
              title={`Scarica ${a.filename}`}
              className="p-0.5 rounded hover:bg-accent/10"
            >
              <Download size={11} className="text-text-muted flex-shrink-0" />
            </span>
          </button>
        );
      })}
      {preview && (
        <FilePreviewModal
          open
          onClose={() => setPreview(null)}
          filename={preview.filename}
          fetchPath={`/support/attachments/${preview.file_id}?inline=1`}
          onDownload={() => downloadAttachment(preview.file_id, preview.filename)}
        />
      )}
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Modal: nuovo ticket
// ───────────────────────────────────────────────────────────────────

const NewTicketModal = ({ open, onClose, onCreated }) => {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("problema");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setSubject(""); setCategory("problema"); setDescription(""); setFiles([]); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) return toast.error("L'oggetto deve avere almeno 3 caratteri.");
    if (description.trim().length < 5) return toast.error("La descrizione deve avere almeno 5 caratteri.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", {
        subject: subject.trim(),
        category,
        description: description.trim(),
      });
      // Upload allegati dopo la creazione (agganciati al primo messaggio)
      if (files.length && data?.first_message_id) {
        const ok = await uploadTicketAttachments(data.id, data.first_message_id, files);
        if (ok > 0) {
          toast.success(
            ok === files.length
              ? "Ticket aperto e allegati caricati."
              : `Ticket aperto. ${ok}/${files.length} allegati caricati.`
          );
        } else {
          toast.success("Ticket aperto. Nessun allegato caricato.");
        }
      } else {
        toast.success("Ticket aperto. Ti risponderemo al più presto.");
      }
      onCreated && onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile aprire il ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wl-modal-overlay" data-testid="new-ticket-modal">
      <div className="wl-modal wl-modal--md" role="dialog" aria-modal="true" aria-labelledby="new-ticket-title">
        <form onSubmit={submit} className="flex flex-col min-h-0">
          <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <LifeBuoy size={18} className="text-accent" />
              <h3 id="new-ticket-title" className="wl-modal__title">Apri un ticket al supporto Wattline</h3>
            </div>
            <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="close-new-ticket" aria-label="Chiudi">
              <X size={16} />
            </button>
          </div>

          <div className="px-6 pb-2 space-y-4 overflow-y-auto">
            <div className="wl-field">
              <span className="wl-label wl-label--required">Categoria</span>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(CATEGORY).map(([key, c]) => {
                  const Icon = c.icon;
                  const active = category === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCategory(key)}
                      data-testid={`category-${key}`}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                        active ? `${c.color} border-current` : "bg-surface border-border text-text-secondary hover:border-accent"
                      }`}
                    >
                      <Icon size={13} strokeWidth={2} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="wl-field">
              <label htmlFor="ticket-subject" className="wl-label wl-label--required">Oggetto</label>
              <input
                id="ticket-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="es. La Visura non scarica il PDF"
                maxLength={120}
                data-testid="ticket-subject"
                className="wl-input"
              />
              <span className="wl-helper wl-helper--muted">Sintetizza il problema in una frase.</span>
            </div>

            <div className="wl-field">
              <label htmlFor="ticket-description" className="wl-label wl-label--required">Descrizione</label>
              <textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Spiega cosa è successo, cosa ti aspettavi e i passi per riprodurre il problema (se applicabile)."
                rows={6}
                maxLength={4000}
                data-testid="ticket-description"
                className="wl-textarea"
              />
              <span className="wl-helper wl-helper--muted">{description.length}/4000 caratteri</span>
            </div>

            <div className="wl-field">
              <span className="wl-label">Allegati</span>
              <AttachmentPicker
                files={files}
                setFiles={setFiles}
                disabled={submitting}
                testIdPrefix="new-ticket-att"
              />
              <span className="wl-helper">Opzionale — PDF, Word, Excel, CSV, immagini · max 25 MB (immagini 10 MB) · fino a 5 file.</span>
            </div>
          </div>

          <div className="wl-modal__actions">
            <button type="button" onClick={onClose} disabled={submitting} className="wl-btn wl-btn--ghost" data-testid="cancel-new-ticket">
              Annulla
            </button>
            <button type="submit" disabled={submitting} className="wl-btn wl-btn--primary" data-testid="submit-new-ticket">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Apri ticket
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Thread del ticket
// ───────────────────────────────────────────────────────────────────

const TicketThread = ({ ticket, onClose, onChanged, currentUser, isSuperAdmin }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [confirmResolve, setConfirmResolve] = useState(false);

  const load = useCallback(async () => {
    if (!ticket) return;
    try {
      const { data } = await api.get(`/support/tickets/${ticket.id}`);
      setDetail(data);
      // Il server ha appena marcato il ticket come letto: aggiorna subito i badge
      window.dispatchEvent(new Event("wl-unread-refresh"));
    } catch (e) {
      toast.error("Impossibile caricare il ticket.");
    } finally {
      setLoading(false);
    }
  }, [ticket]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e?.preventDefault();
    if (!reply.trim() && replyFiles.length === 0) return;
    if (!reply.trim()) {
      return toast.error("Aggiungi un testo al messaggio (anche breve).");
    }
    setSending(true);
    try {
      const { data: msg } = await api.post(`/support/tickets/${ticket.id}/messages`, { body: reply.trim() });
      // Upload allegati dopo la reply
      if (replyFiles.length && msg?.id) {
        const ok = await uploadTicketAttachments(ticket.id, msg.id, replyFiles);
        if (ok > 0 && ok < replyFiles.length) {
          toast.warning(`Solo ${ok}/${replyFiles.length} allegati caricati.`);
        }
      }
      setReply("");
      setReplyFiles([]);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio messaggio.");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.patch(`/support/tickets/${ticket.id}/status`, { status: newStatus });
      toast.success(`Stato aggiornato: ${STATUS[newStatus].label}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore cambio stato.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  /** Wrapper: la transizione a "risolto" chiude il ticket e blocca ulteriori
   *  messaggi dall'utente. Chiediamo conferma esplicita via ConfirmModal. */
  const handleStatusClick = (newStatus) => {
    if (newStatus === "risolto") {
      setConfirmResolve(true);
    } else {
      changeStatus(newStatus);
    }
  };

  const runResolve = async () => {
    await changeStatus("risolto");
    setConfirmResolve(false);
  };

  if (!ticket) return null;

  const status = detail?.status || ticket.status;
  const isResolved = status === "risolto";

  return (
    <div className="wl-modal-overlay" data-testid="ticket-thread-modal">
      <div className="wl-modal wl-modal--lg" role="dialog" aria-modal="true" aria-labelledby="thread-title" style={{maxHeight: '90vh'}}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <CategoryBadge category={detail?.category || ticket.category} />
              <StatusBadge status={status} />
              {isSuperAdmin && (
                <span className="inline-flex items-center gap-1 text-[11px] text-text-muted">
                  <Building2 size={11} /> {detail?.org_name || ticket.org_name}
                </span>
              )}
            </div>
            <h3 id="thread-title" className="text-sm font-semibold text-text-primary truncate" data-testid="thread-subject">
              {detail?.subject || ticket.subject}
            </h3>
            <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-1.5">
              <UserAvatar
                userId={detail?.opener_user_id || ticket.opener_user_id}
                name={detail?.opener_user_name || ticket.opener_user_name}
                size={16}
                testId="thread-opener-avatar"
              />
              <span>Aperto da {detail?.opener_user_name || ticket.opener_user_name} · {fmtDateTime(detail?.created_at || ticket.created_at)}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="close-thread" aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>

        {/* Status controls (super_admin only) */}
        {isSuperAdmin && (
          <div className="px-5 py-3 border-b border-border bg-subtle/40">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-text-muted font-medium">Cambia stato:</span>
              {Object.entries(STATUS).map(([key, s]) => {
                const active = status === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => !active && !updatingStatus && handleStatusClick(key)}
                    disabled={active || updatingStatus}
                    data-testid={`set-status-${key}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                      active ? `${s.className} cursor-default` : "bg-surface border-border text-text-secondary hover:border-accent"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="thread-messages">
          {loading ? (
            <div className="text-sm text-text-muted text-center py-8">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Caricamento…
            </div>
          ) : (detail?.messages || []).length === 0 ? (
            <div className="text-sm text-text-muted text-center py-8">Nessun messaggio.</div>
          ) : (
            (detail.messages).map((m) => {
              const fromWattline = m.author_role === "super_admin";
              const isSelf = m.author_user_id === currentUser?.id;
              const align = isSelf ? "items-end" : "items-start";
              const bubble = fromWattline
                ? "bg-accent/10 border-accent/30 text-text-primary"
                : isSelf
                  ? "bg-blue-50 border-blue-200 text-text-primary"
                  : "bg-subtle border-border text-text-primary";
              return (
                <div key={m.id} className={`flex flex-col ${align}`} data-testid={`message-${m.id}`}>
                  <div className={`max-w-[85%] border rounded-lg px-3 py-2 ${bubble}`}>
                    <div className="text-[11px] font-medium mb-1 flex items-center gap-1.5">
                      {fromWattline && <span className="inline-flex items-center gap-1 text-accent"><LifeBuoy size={11} /> Wattline</span>}
                      {!fromWattline && (
                        <span className="inline-flex items-center gap-1.5">
                          <UserAvatar userId={m.author_user_id} name={m.author_user_name} size={16} />
                          {m.author_user_name}
                        </span>
                      )}
                      <span className="text-text-muted font-normal">· {fmtRel(m.created_at)}</span>
                    </div>
                    {m.body && (
                      <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                    )}
                    <AttachmentList attachments={m.attachments || []} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Reply */}
        {isResolved ? (
          <div className="px-5 py-4 border-t border-border bg-subtle/40 text-center text-xs text-text-muted">
            <CheckCircle2 size={14} className="inline mr-1 text-green-600" />
            Ticket risolto. Per nuove segnalazioni apri un nuovo ticket.
          </div>
        ) : (
          <form onSubmit={send} className="px-5 py-4 border-t border-border space-y-2">
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={isSuperAdmin ? "es. Ho preso in carico il tuo caso, vediamo…" : "es. Ho provato ma continua a dare errore 500…"}
                rows={2}
                maxLength={4000}
                data-testid="reply-input"
                className="wl-textarea flex-1"
              />
              <button
                type="submit"
                disabled={sending || !reply.trim()}
                data-testid="send-reply"
                className="wl-btn wl-btn--primary"
                aria-label="Invia risposta"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                <span className="hidden sm:inline">Invia</span>
              </button>
            </div>
            <AttachmentPicker
              files={replyFiles}
              setFiles={setReplyFiles}
              disabled={sending}
              testIdPrefix="reply-att"
            />
          </form>
        )}
        <ConfirmModal
          open={confirmResolve}
          onOpenChange={(v) => { if (!v && !updatingStatus) setConfirmResolve(false); }}
          title={`Segnare il ticket "${detail?.subject || ticket.subject}" come risolto?`}
          description="Il cliente non potrà più rispondere a questo ticket. Se avesse bisogno, dovrà aprirne uno nuovo."
          variant="primary"
          confirmLabel="Segna come risolto"
          cancelLabel="Non ancora"
          loading={updatingStatus}
          onConfirm={runResolve}
          testId="confirm-resolve"
        />
      </div>
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Lista ticket (operatore/admin)
// ───────────────────────────────────────────────────────────────────

const MyTicketsList = ({ tickets, onOpen, loading, failed, onRetry }) => {
  if (loading) {
    return <div className="text-sm text-text-muted py-6 text-center"><Loader2 size={18} className="animate-spin mx-auto mb-1" />Caricamento…</div>;
  }
  if (failed) {
    return <LoadErrorBox what="i tuoi ticket" onRetry={onRetry} testId="my-tickets-error" />;
  }
  if (!tickets.length) {
    return (
      <div className="text-sm text-text-muted py-10 text-center" data-testid="my-tickets-empty">
        <Inbox size={28} className="mx-auto mb-2 opacity-50" />
        Nessun ticket aperto. Apri il primo quando hai bisogno di noi.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border" data-testid="my-tickets-list">
      {tickets.map((t) => (
        <li key={t.id}>
          <button
            type="button"
            onClick={() => onOpen(t)}
            data-testid={`ticket-row-${t.id}`}
            className="w-full text-left py-3 px-1 hover:bg-subtle/50 transition-colors flex items-start gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <CategoryBadge category={t.category} />
                <StatusBadge status={t.status} />
              </div>
              <div className="text-sm font-medium text-text-primary truncate">{t.subject}</div>
              <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2">
                <MessageSquare size={11} /> {t.message_count} msg
                <span>·</span>
                <Clock3 size={11} /> {fmtRel(t.last_message_at)}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};


// ───────────────────────────────────────────────────────────────────
// Vista super_admin: tabella + filtro stato
// ───────────────────────────────────────────────────────────────────

const SuperAdminTicketsList = ({ onOpen, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(
    () => new URLSearchParams(window.location.search).get("filtro") || "da_gestire");
  const [loading, setLoading] = useState(true);

  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/support/tickets${q}`);
      setItems(data || []);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Conteggi dal server: la STESSA interrogazione che alimenta la dashboard
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get("/support/tickets/conteggi").then(({ data }) => setCounts(data)).catch(() => setCounts(null));
  }, [refreshKey, filter]);

  return (
    <Section
      title="Tutti i ticket di supporto"
      icon={LifeBuoy}
      action={
        <div className="flex gap-1 text-xs flex-wrap">
          {[
            ["da_gestire", "Da gestire"],
            ["aperto", "Aperti"],
            ["in_lavorazione", "In lavorazione"],
            ["risolto", "Risolti"],
            ["all", "Tutti"],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`sa-filter-${v}`}
              className={`tab-neon-pill px-2.5 py-1 rounded-md ${filter === v ? "is-active" : "bg-subtle text-text-secondary"}`}
            >{l}</button>
          ))}
        </div>
      }
    >
      {counts && (
        <div className="text-[11px] text-text-muted mb-3 flex gap-3 flex-wrap" data-testid="sa-ticket-counts">
          <span>Da gestire: <b className="text-text-primary">{counts.da_gestire}</b></span>
          <span>Aperti: <b className="text-text-primary">{counts.aperto}</b></span>
          <span>In lavorazione: <b className="text-text-primary">{counts.in_lavorazione}</b></span>
          <span>Risolti: <b className="text-text-primary">{counts.risolto}</b></span>
        </div>
      )}
      {loading ? (
        <div className="text-sm text-text-muted py-6 text-center"><Loader2 size={18} className="animate-spin mx-auto mb-1" />Caricamento…</div>
      ) : failed ? (
        <LoadErrorBox what="i ticket" onRetry={load} testId="sa-tickets-error" />
      ) : !items.length ? (
        <div className="text-sm text-text-muted py-10 text-center">
          <Inbox size={28} className="mx-auto mb-2 opacity-50" />
          Nessun ticket in questa vista.
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border" data-testid="sa-tickets-list">
            {items.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => onOpen(t)}
                  data-testid={`sa-ticket-row-${t.id}`}
                  className="w-full text-left py-3 px-1 hover:bg-subtle/50 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <CategoryBadge category={t.category} />
                    <StatusBadge status={t.status} />
                    <span className="inline-flex items-center gap-1 text-[11px] text-text-muted">
                      <Building2 size={11} /> {t.org_name}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-text-primary truncate">{t.subject}</div>
                  <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5">
                      <UserAvatar userId={t.opener_user_id} name={t.opener_user_name} size={16} />
                      {t.opener_user_name}
                    </span>
                    <span>·</span>
                    <MessageSquare size={11} /> {t.message_count} msg
                    <span>·</span>
                    <Clock3 size={11} /> {fmtRel(t.last_message_at)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
};


// ───────────────────────────────────────────────────────────────────
// Pagina principale
// ───────────────────────────────────────────────────────────────────

export default function Support() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [myTickets, setMyTickets] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Apertura ticket = letto lato server: rifetcha le liste per coerenza visiva
  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener("wl-unread-refresh", bump);
    return () => window.removeEventListener("wl-unread-refresh", bump);
  }, []);

  const [mineFailed, setMineFailed] = useState(false);
  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const { data } = await api.get("/support/tickets/me");
      setMyTickets(data || []);
      setMineFailed(false);
    } catch {
      setMineFailed(true);
    } finally {
      setLoadingMine(false);
    }
  }, []);

  useEffect(() => { if (!isSuperAdmin) loadMine(); else setLoadingMine(false); }, [loadMine, isSuperAdmin, refreshKey]);

  const handleCreated = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleThreadChanged = () => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <LifeBuoy size={22} className="text-accent" /> Supporto
          </h1>
          <p className="text-sm text-text-muted mt-1">
            {isSuperAdmin
              ? "Gestisci i ticket di supporto inviati dalle aziende clienti."
              : "Apri un ticket per segnalare problemi, richieste o domande al team Wattline."}
          </p>
        </div>
        {!isSuperAdmin && (
          <Button
            onClick={() => setShowNew(true)}
            data-testid="open-new-ticket-btn"
            className="bg-accent hover:bg-accent/90 text-white"
          >
            <Plus size={14} className="mr-1" /> Nuovo ticket
          </Button>
        )}
      </div>

      {/* Banner info per il cliente */}
      {!isSuperAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-900">
            <b>Come funziona.</b> Apri un ticket scegliendo la categoria, descrivi il caso e ricevi risposta dal team Wattline.
            Lo trovi sempre qui con lo stato aggiornato (Aperto · In lavorazione · Risolto). Le notifiche compaiono nel pallino sulla sidebar.
          </div>
        </div>
      )}

      {/* Vista super_admin */}
      {isSuperAdmin ? (
        <SuperAdminTicketsList onOpen={setOpenTicket} refreshKey={refreshKey} />
      ) : (
        <Section title="I miei ticket" icon={Inbox}>
          <MyTicketsList tickets={myTickets} onOpen={setOpenTicket} loading={loadingMine} failed={mineFailed} onRetry={loadMine} />
        </Section>
      )}

      <NewTicketModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(t) => { handleCreated(); setOpenTicket(t); }}
      />

      {openTicket && (
        <TicketThread
          ticket={openTicket}
          onClose={() => setOpenTicket(null)}
          onChanged={handleThreadChanged}
          currentUser={user}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </div>
  );
}
